/**
 * Client-side validation for notification template drafts.
 *
 * Validates:
 *   - Required fields (name, subject, body)
 *   - Email syntax if sender config is enabled
 *   - Max subject/body length
 *   - Duplicate custom variable keys
 *   - Invalid variable key format
 *   - Variables referenced in template but unknown to catalog/custom
 *   - Unsafe HTML content
 *   - Empty body variants
 */
import type {
  NotificationTemplateDraft,
  NotificationValidationError,
  NotificationValidationResult,
} from '../notification-template.types';
import { EMAIL_REGEX } from '../notification-template.constants';
import { extractVariableKeys } from './notification-template-renderer';
import { validateHtmlSafety } from './notification-html-sanitizer';
import { BUILTIN_VAR_PREVIEW_DEFAULTS } from './notification-variable-resolver';

const MAX_SUBJECT_LENGTH = 200;
const MAX_BODY_LENGTH = 100_000;
const VARIABLE_KEY_PATTERN = /^[a-zA-Z][a-zA-Z0-9_.]*$/;

export function validateNotificationDraft(
  draft: NotificationTemplateDraft,
): NotificationValidationResult {
  const errors: NotificationValidationError[] = [];

  if (!draft.name.trim()) {
    errors.push({ path: 'name', message: 'Name is required' });
  }

  if (!draft.subject.trim()) {
    errors.push({ path: 'subject', message: 'Subject is required' });
  } else if (draft.subject.length > MAX_SUBJECT_LENGTH) {
    errors.push({ path: 'subject', message: `Subject must be ${MAX_SUBJECT_LENGTH} characters or less` });
  }

  if (!draft.htmlBody.trim() || isEmptyHtml(draft.htmlBody)) {
    errors.push({ path: 'htmlBody', message: 'Email body is required' });
  } else if (draft.htmlBody.length > MAX_BODY_LENGTH) {
    errors.push({ path: 'htmlBody', message: `Body exceeds maximum length of ${MAX_BODY_LENGTH} characters` });
  }

  const fromEmail = draft.from?.email?.trim() ?? '';
  if (fromEmail && !EMAIL_REGEX.test(fromEmail)) {
    errors.push({ path: 'from.email', message: 'From email must be a valid email address' });
  }

  // HTML safety
  if (draft.htmlBody.trim()) {
    const htmlIssues = validateHtmlSafety(draft.htmlBody);
    for (const issue of htmlIssues) {
      if (issue.type === 'error') {
        errors.push({ path: 'htmlBody', message: issue.message });
      }
    }
  }

  // Variable validation
  const variableKeys = draft.variables.map((v) => v.key);
  const keySet = new Set<string>();
  for (let i = 0; i < draft.variables.length; i++) {
    const v = draft.variables[i];
    if (!v.key.trim()) {
      errors.push({ path: `variables[${i}].key`, message: 'Variable key is required' });
      continue;
    }
    if (!VARIABLE_KEY_PATTERN.test(v.key)) {
      errors.push({
        path: `variables[${i}].key`,
        message: `Variable key "${v.key}" is invalid. Use letters, numbers, dots, and underscores (start with a letter).`,
      });
    }
    if (keySet.has(v.key)) {
      errors.push({ path: `variables[${i}].key`, message: `Duplicate variable key: "${v.key}"` });
    }
    keySet.add(v.key);
  }

  // Check for template tokens that are unknown to catalog/custom variables
  const allTemplateTokens = new Set([
    ...extractVariableKeys(draft.subject),
    ...extractVariableKeys(draft.htmlBody),
    ...extractVariableKeys(draft.textBody ?? ''),
  ]);
  const knownKeys = new Set([
    ...variableKeys,
    ...Object.keys(BUILTIN_VAR_PREVIEW_DEFAULTS),
  ]);
  for (const token of allTemplateTokens) {
    if (!knownKeys.has(token)) {
      const matchingVar = draft.variables.find((v) => v.key === token);
      if (!matchingVar) {
        errors.push({
          path: 'htmlBody',
          message: `Template references unknown variable "{{${token}}}". Add it as a custom variable or use a built-in token.`,
        });
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

const EMPTY_HTML_PATTERNS = [
  /^<p>\s*<\/p>$/i,
  /^<div>\s*<\/div>$/i,
  /^<br\s*\/?>$/i,
  /^(<br\s*\/?\s*>|\s|&nbsp;)*$/i,
];

function isEmptyHtml(html: string): boolean {
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  if (!stripped || stripped.replace(/&nbsp;/g, '').trim() === '') return true;
  const trimmed = html.trim();
  return EMPTY_HTML_PATTERNS.some((p) => p.test(trimmed));
}
